<template lang="html">
  <action-bar id="floorplan-list-actions">
    <!-- Left -->
    <template slot="left">
      <div id="floorplan-grid-actions-left">
        <!-- Back -->
        <div class="d-none d-lg-flex align-items-center" v-if="!showFavourites">
          <advanced-select :label="typeLabel"
          @apply="applyFilters"
          @cancel="revertMutableFilters"
          @reset="resetTypeFilters"
          :requiresApply="true">
            <check-list v-model="mutableFilters.typeList"
              :responsive="true"
            />
          </advanced-select>
          <advanced-select :label="collectionLabel"
          @apply="applyFilters"
          @cancel="revertMutableFilters"
          @reset="resetCollectionFilters"
          :requiresApply="true">
            <check-list v-model="mutableFilters.collectionList"
              :responsive="true"
            />
          </advanced-select>
          <advanced-select :label="exposureLabel"
          @apply="applyFilters"
          @cancel="revertMutableFilters"
          @reset="resetExposureFilters"
          :requiresApply="true">
            <check-list v-model="mutableFilters.exposureList"
              :responsive="true"
            />
          </advanced-select>
          <advanced-select label="More Filters"
          @apply="applyFilters"
          @cancel="revertMutableFilters"
          @reset="resetMoreFilters"
          :requiresApply="true"
          :fillWidth="true"
          class="more-filters">
            <check-list v-if="mutableFilters.bathList.length > 1"
            v-model="mutableFilters.bathList"
            title="Bathrooms"
            :responsive="true"
            class="mb-4"
            />

            <range-slider title="Size"
            :range="{
              min: mutableFilters.sqftRange.min,
              max: mutableFilters.sqftRange.max
            }"
            :defaultRange="mutableFilters.sqftRange.default"
            :increments="50"
            unitLabel="sqft"
            @change="updateSqftRange"
            class="mb-4" />
            <range-slider title="Floors"
            :range="{
              min: mutableFilters.floorRange.min,
              max: mutableFilters.floorRange.max
            }"
            :defaultRange="mutableFilters.floorRange.default"
            @change="updateFloorRange"
            class="mb-4" />
          </advanced-select>
        </div>
        <div class="d-flex d-lg-none" v-if="!showFavourites">
          <advanced-select label="Filters"
          @apply="applyFilters"
          @cancel="revertMutableFilters"
          @reset="resetAllFilters"
          :requiresApply="true"
          :fillWidth="true">
            <check-list v-model="mutableFilters.typeList"
            title="Type"
            :responsive="true" />
            <check-list v-model="mutableFilters.exposureList"
            title="View"
            :responsive="true" />
            <check-list v-if="mutableFilters.bathList.length > 1"
            v-model="mutableFilters.bathList"
            title="Bathrooms"
            :responsive="true" />
            <range-slider title="Size"
            :range="{
              min: mutableFilters.sqftRange.min,
              max: mutableFilters.sqftRange.max
            }"
            :defaultRange="mutableFilters.sqftRange.default"
            :increments="50"
            unitLabel="sqft"
            @change="updateSqftRange"
            class="mb-4" />
            <range-slider title="Floors"
            :range="{
              min: mutableFilters.floorRange.min,
              max: mutableFilters.floorRange.max
            }"
            :defaultRange="mutableFilters.floorRange.default"
            @change="updateFloorRange"
            class="mb-4" />
          </advanced-select>
          <text-input
          v-model="mutableFilters.searchString"
          @textChanged="applyFilters"
          @reset="resetSearchString"
          />
        </div>
        <div v-if="showFavourites">
          <router-link to="/" class="btn btn-link-action">
            <i class="far fa-angle-left mr-2"></i> Floorplan list
          </router-link>
          <button
          class="btn btn-link-action d-none d-md-inline-block"
          @click="emailFloorplanFavs()">
            <i class="fas fa-file-pdf"></i> Email Favourites
          </button>
        </div>
      </div>
    </template>

    <!-- Right -->
    <template slot="right">
      <div id="floorplan-list-actions-right" class="d-flex align-items-center">
        <button v-if="listDisplayType === 'grid'"
        class="btn btn-link-action
        d-none d-xl-flex align-items-center"
        @click="toggleSort()">
          <span>Sort Sqft</span>
          <span class="ml-1" style="font-size: 0.8rem">
            <i class="far fa-long-arrow-down" v-if="sqftSort === 'dsc'"></i>
            <i class="far fa-long-arrow-up" v-else></i>
          </span>
        </button>
        <div class="view-toggle
        d-none d-lg-flex align-items-center
        mr-3"
        @click="toggleListDisplayType()">
          <i class="fas fa-th-large"
          :class="{'view-active': listDisplayType === 'grid' }"></i>
          <span class="view-toggle-divider mx-1"></span>
          <i class="fas fa-list"
          :class="{'view-active': listDisplayType === 'table' }"></i>
        </div>
        <compare-btn />
      </div>
    </template>
  </action-bar>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex';
import { create } from 'vue-modal-dialogs';
import Email from '@/services/email';
import KeyboardInputDialog from '@/components/Dialogs/KeyboardInputDialog';
import CompareButtonComponent from '@/components/Floorplan/CompareButton';
import SingleSelectComponent from '@/components/Input/SingleSelect';
import CheckListComponent from '@/components/Input/Checklist';
import RangeSliderComponent from '@/components/input/RangeSlider';
import AdvanceSelectComponent from '@/components/Input/AdvanceSelect';
import ActionBar from '@/components/ActionBar';
import TextInput from '@/components/Input/TextInput';
import fstore from '@/analytics/firestore';

const keyboardDialog = create(KeyboardInputDialog, 'prevText');

export default {
  data() {
    return {
      mutableFilters: {},
      sqftSort: 'asc',
      firstLoad: true,
    };
  },
  computed: {
    ...mapState({
      filters: state => state.floorplans.filters,
      showFavourites: state => state.floorplans.showFavourites,
      listDisplayType: state => state.floorplans.listDisplayType,
      isTouchscreen: state => state.project.isTouchscreen,
      pageReady: state => state.ui.pageReady
    }),
    ...mapGetters({
      deviceLg: 'ui/deviceLg',
      filteredFloorplanList: 'floorplans/filteredFloorplanList'
    }),
    typeLabel() {
      return this.getLabelByType('typeList', 'Type');
    },
    exposureLabel() {
      return this.getLabelByType('exposureList', 'Exposure');
    },
    collectionLabel() {
      return this.getLabelByType('collectionList', 'Suites');
    }
  },
  methods: {
    ...mapMutations({
      setFilters: 'floorplans/setFilters',
      toggleListDisplayType: 'floorplans/toggleListDisplayType',
      sortFloorplanListAsc: 'floorplans/sortFloorplanListAsc',
      sortFloorplanListDsc: 'floorplans/sortFloorplanListDsc'
    }),
    getLabelByType(filterType, filterLabel) {
      const activeFilters = this.getActiveFiltersByType(filterType);
      const activeCount = activeFilters.length;
      if (activeCount === 1) return activeFilters[0].label;
      else if (activeCount > 1) return `${filterLabel} (${activeCount})`;
      return filterLabel;
    },
    getActiveFiltersByType(filterType) {
      return this.mutableFilters[filterType].filter(filter => filter.active);
    },
    applyFilters() {
      // console.log('list actions apply filters');
      this.setFilters(this.mutableFilters);
    },
    resetTypeFilters() {
      this.mutableFilters.typeList.forEach((type) => { type.active = false; });
    },
    resetCollectionFilters() {
      this.mutableFilters.collectionList.forEach((collection) => { collection.active = false; });
    },
    resetExposureFilters() {
      this.mutableFilters.exposureList.forEach((exposure) => { exposure.active = false; });
    },
    revertMutableFilters() {
      this.mutableFilters = JSON.parse(JSON.stringify(this.filters));
      if (this.firstLoad) {
        this.processSuitesQuery();
        this.firstLoad = false;
      }
    },
    resetMoreFilters() {
      this.resetExposureFilters();
      this.mutableFilters.bathList.forEach((bath) => { bath.active = false; });
      this.mutableFilters.sqftRange.min = this.mutableFilters.sqftRange.default.min;
      this.mutableFilters.sqftRange.max = this.mutableFilters.sqftRange.default.max;
      this.mutableFilters.floorRange.min = this.mutableFilters.floorRange.default.min;
      this.mutableFilters.floorRange.max = this.mutableFilters.floorRange.default.max;
    },
    updateSqftRange(newSqftRange) {
      this.mutableFilters.sqftRange.min = newSqftRange.min;
      this.mutableFilters.sqftRange.max = newSqftRange.max;
    },
    updateFloorRange(newFloorRange) {
      this.mutableFilters.floorRange.min = newFloorRange.min;
      this.mutableFilters.floorRange.max = newFloorRange.max;
    },
    resetAllFilters() {
      this.resetTypeFilters();
      this.resetCollectionFilters();
      this.resetExposureFilters();
      this.resetMoreFilters();
    },
    resetSearchString() {
      this.mutableFilters.searchString = '';
      this.applyFilters();
    },
    toggleSort() {
      if (this.sqftSort === 'asc') {
        this.sqftSort = 'dsc';
        this.sortFloorplanListDsc();
      } else {
        this.sqftSort = 'asc';
        this.sortFloorplanListAsc();
      }
    },
    trackConversion(email) {
      const plans = this.filteredFloorplanList.map(plan => ({
        name: plan.name,
        interior: plan.interior,
        beds: plan.beds,
        den: plan.dens > 0,
        baths: plan.baths,
        exposures: plan.exposure
      }));
      fstore.trackPlansConverted(plans);
      fstore.trackLeads(email, plans);
    },
    async emailFloorplanFavs() {
      const email = await keyboardDialog(this.text);
      if (email) {
        this.$toasted.show('Emailing floorplan...', {
          position: 'top-center',
          duration: 4000
        });
        const emailUrlArr = [];
        const emailUrlName = [];
        this.filteredFloorplanList.forEach((plan) => {
          emailUrlArr.push(plan.assets.pdf.url);
          emailUrlName.push(plan.name);
        });
        const result = await Email.send(email,
          this.filteredFloorplanList);
        this.$toasted.clear();
        if (!this.isTouchscreen) {
          this.trackConversion(email);
        }
        if (result.status === 'success') {
          this.$toasted.show(result.msg, {
            theme: 'success',
            position: 'top-center',
            duration: 4000,
            type: 'success'
          });
        } else {
          this.$toasted.show(result.msg, {
            theme: 'error',
            position: 'top-center',
            duration: 4000,
            type: 'error'
          });
        }
      }
    },
    processSuitesQuery() {
      // console.log('process suites query?');
      if (this.mutableFilters && this.mutableFilters.collectionList && this.$route.query.suites) {
        const val = this.$route.query.suites;
        if (this.mutableFilters.collectionList) {
          this.mutableFilters.collectionList.forEach((collection) => {
            if (collection.slug === val) {
              collection.active = true;
            }
          });
          this.applyFilters();
        }
      }
    }
  },
  created() {
    this.revertMutableFilters();
  },
  watch: {
    filters() {
      this.revertMutableFilters();
    },
    deviceLg() {
      if (!this.deviceLg && this.listDisplayType === 'table') {
        this.toggleListDisplayType();
      }
    },
    pageReady: {
      immediate: true,
      handler() {
        this.processSuitesQuery();
      }
    }
  },
  components: {
    'compare-btn': CompareButtonComponent,
    'single-select': SingleSelectComponent,
    'check-list': CheckListComponent,
    'range-slider': RangeSliderComponent,
    'advanced-select': AdvanceSelectComponent,
    'action-bar': ActionBar,
    'text-input': TextInput
  }
};
</script>

<style lang="scss">
#floorplan-list-actions {
  background-color: white;
  -webkit-box-shadow: 0px -1px 5px 0px rgba(0,0,0,0.15);
  -moz-box-shadow: 0px -1px 5px 0px rgba(0,0,0,0.15);
  box-shadow: 0px -1px 5px 0px rgba(0,0,0,0.15);

  #floorplan-grid-actions-left {
    margin-left: -12px;
  }

  .view-toggle {
    cursor: pointer;

    i {
      color: hsl(0, 0, 70%);

      &:hover, &:active {
        color: var(--action);
      }
    }

    .view-active {
      color: var(--action);
    }
  }

  .view-toggle-divider {
    display: inline-block;
    height: 20px;
    width: 1px;
    background: hsl(0, 0, 90%);
  }
}
</style>



// WEBPACK FOOTER //
// src/pages/FloorplansPage/FloorplanList/FloorplanListActions.vue